"use client";

import { format as formatDate } from "date-fns";

import { useEstimator } from "../../_context/EstimatorContext";
import type { CityEstimate } from "../../_logic/types";
import { SourcePopover } from "./SourcePopover";

/**
 * A city's "Venue rates · 110 venues" caption, opening how the range was made: the median
 * published nightly rates of the venues we list there, when they were updated, and a few of
 * those venues with their own rates.
 * @param props.city - The city's estimate (venue-rate hotel).
 * @param props.short - Just "110 venues", for narrow columns.
 * @returns The caption with its source.
 * @throws Never.
 */
export const HotelSource = ({ city, short = false }: { city: CityEstimate; short?: boolean }) => {
  const { money } = useEstimator();
  const { hotel } = city;
  const venues = `${hotel.venueCount} ${hotel.venueCount === 1 ? "venue" : "venues"}`;
  return (
    <SourcePopover
      trigger={short ? venues : `Venue rates · ${venues}`}
      label={`Where ${city.city}'s hotel range comes from`}
      title={`Hotel rates in ${city.city}`}
    >
      <p className="text-gray-600">
        The median published nightly rates of the {venues} we list in {city.city}
        {hotel.perNight ? `: ${money.formatRange(hotel.perNight)} a night` : ""}, before group discounts and taxes.
        {hotel.updatedAt ? ` Updated ${formatDate(new Date(hotel.updatedAt), "MMM d, yyyy")}.` : ""}
      </p>
      {hotel.samples && hotel.samples.length > 0 && (
        <ul className="flex list-none flex-col divide-y divide-gray-100 border-t border-gray-100">
          {hotel.samples.map((sample) => (
            <li key={sample.name} className="flex items-baseline justify-between gap-3 py-1.5 text-xs">
              <span title={sample.name} className="min-w-0 truncate text-gray-700">
                {sample.name}
              </span>
              <span className="shrink-0 tabular-nums text-gray-500">
                {money.formatRange({ low: sample.low, high: sample.high })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SourcePopover>
  );
};
